import { z } from "zod";
import { assertUnreachable } from "../helpers/utilities.js";
import { recurringScheduleSchema } from "../scheduling/recurringSchedule.js";
import { groupIdSchema } from "./group.js";
import { playbookIdSchema } from "./playbook.js";
import { teammateIdSchema } from "./teammate.js";
const automationIdSchema = z.uuid().brand();
function asAutomationId(id) {
  return automationIdSchema.parse(id);
}
function encodeAutomationResourceId(companyId, automationId) {
  return `company:${companyId}:automation:${automationId}`;
}
const AutomationTemplateIds = {
  TEAMMATE_COACHING: "teammate-coaching",
  CUSTOMER_PRAISE_DIGEST: "customer-praise-digest",
  PERFECT_CALLS: "perfect-calls",
  PLAYBOOK_SCORE_MOVEMENT: "playbook-score-movement",
  LEAD_BURNING: "lead-burning",
  CUSTOM: "custom"
};
const allAutomationTemplateIds = [
  AutomationTemplateIds.TEAMMATE_COACHING,
  AutomationTemplateIds.CUSTOMER_PRAISE_DIGEST,
  AutomationTemplateIds.PERFECT_CALLS,
  AutomationTemplateIds.PLAYBOOK_SCORE_MOVEMENT,
  AutomationTemplateIds.LEAD_BURNING,
  AutomationTemplateIds.CUSTOM
];
const automationTemplateIdSchema = z.enum(allAutomationTemplateIds);
const LEAD_BURNING_DAILY_TIME_OF_DAY = "20:00";
const allAutomationManagerScopes = [
  "all_managers",
  "specific_managers"
];
const automationManagerScopeSchema = z.enum(allAutomationManagerScopes);
const automationManagerRecipientsSchema = z.discriminatedUnion("scope", [
  z.object({ scope: z.literal("all_managers") }),
  z.object({
    scope: z.literal("specific_managers"),
    teammate_ids: z.array(teammateIdSchema)
  })
]);
const automationSubscriberRecipientsSchema = z.object({
  teammate_ids: z.array(teammateIdSchema)
});
const AutomationDeliveryProvider = {
  EMAIL: "email",
  SLACK: "slack",
  TEAMS: "teams"
};
const automationDeliveryProviderSchema = z.enum([
  AutomationDeliveryProvider.EMAIL,
  AutomationDeliveryProvider.SLACK,
  AutomationDeliveryProvider.TEAMS
]);
const automationProviderChannelSchema = z.object({
  id: z.string(),
  name: z.string().nullable()
});
const automationChatDeliveryModeSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("direct_message") }),
  z.object({
    kind: z.literal("channel"),
    channels: z.array(automationProviderChannelSchema)
  })
]);
const automationDeliverySchema = z.discriminatedUnion("provider", [
  z.object({ provider: z.literal(AutomationDeliveryProvider.EMAIL) }),
  z.object({
    provider: z.literal(AutomationDeliveryProvider.SLACK),
    destination: automationChatDeliveryModeSchema
  }),
  z.object({
    provider: z.literal(AutomationDeliveryProvider.TEAMS),
    destination: automationChatDeliveryModeSchema
  })
]);
function buildDefaultAutomationDeliveryForProvider(provider) {
  switch (provider) {
    case AutomationDeliveryProvider.EMAIL:
      return { provider };
    case AutomationDeliveryProvider.SLACK:
    case AutomationDeliveryProvider.TEAMS:
      return { provider, destination: { kind: "direct_message" } };
    default:
      return assertUnreachable(provider);
  }
}
const DEFAULT_AUTOMATION_DELIVERY = {
  provider: AutomationDeliveryProvider.EMAIL
};
const teammateCoachingAutomationSettingsSchema = z.object({
  playbook_id: playbookIdSchema,
  /** "Coaching focus" guidance fed into the coaching agent. */
  coaching_focus: z.string(),
  /** "Who should be notified?" — each team member / each manager. */
  should_notify_teammates: z.boolean(),
  should_notify_managers: z.boolean()
});
const automationPlaybooksScopeSchema = z.array(playbookIdSchema).min(1).refine((playbookIds) => new Set(playbookIds).size === playbookIds.length, {
  message: "Playbook ids must be unique"
});
const automationRecipientSelectionSchema = z.object({
  teammate_ids: z.array(teammateIdSchema),
  group_ids: z.array(groupIdSchema)
});
const automationReportDestinationSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("recipients"),
    primary: automationRecipientSelectionSchema,
    subscribers: automationRecipientSelectionSchema
  }),
  z.object({
    type: z.literal("channels"),
    channels: z.array(automationProviderChannelSchema)
  })
]);
const automationMappedEntryDestinationSchema = z.discriminatedUnion(
  "type",
  [
    automationReportDestinationSchema.options[0].omit({ subscribers: true }),
    automationReportDestinationSchema.options[1]
  ]
);
const allOrSpecificTeammatesSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("all") }),
  z.object({
    type: z.literal("specific"),
    teammate_ids: z.array(teammateIdSchema),
    group_ids: z.array(groupIdSchema)
  })
]);
const automationManagerReportInstanceSelectionSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("all") }),
  z.object({
    type: z.literal("specific"),
    teammate_ids: z.array(teammateIdSchema)
  })
]);
const automationGroupReportInstanceSelectionSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("all") }),
  z.object({
    type: z.literal("specific"),
    group_ids: z.array(groupIdSchema)
  })
]);
const automationReportInstancesSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("shared") }),
  z.object({
    type: z.literal("teammates"),
    selection: allOrSpecificTeammatesSchema
  }),
  z.object({
    type: z.literal("managers"),
    selection: automationManagerReportInstanceSelectionSchema
  }),
  z.object({
    type: z.literal("groups"),
    selection: automationGroupReportInstanceSelectionSchema
  })
]);
function buildAutomationScopeReferenceSchema(configuredShape) {
  return z.discriminatedUnion("source", [
    z.object({
      source: z.literal("configured"),
      ...configuredShape
    }),
    z.object({ source: z.literal("instance") })
  ]);
}
const automationTeammateScopeReferenceSchema = buildAutomationScopeReferenceSchema({ teammate_id: teammateIdSchema });
const automationManagerScopeReferenceSchema = buildAutomationScopeReferenceSchema({ manager_id: teammateIdSchema });
const automationGroupScopeReferenceSchema = buildAutomationScopeReferenceSchema(
  { group_id: groupIdSchema }
);
const automationReportScopeRuleSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("playbook_teammates"),
    playbook_ids: automationPlaybooksScopeSchema,
    teammate_selection: allOrSpecificTeammatesSchema.optional()
  }),
  z.object({
    type: z.literal("teammates"),
    teammate: automationTeammateScopeReferenceSchema,
    playbook_ids: automationPlaybooksScopeSchema
  }),
  z.object({
    type: z.literal("manager_teammates"),
    manager: automationManagerScopeReferenceSchema,
    playbook_ids: automationPlaybooksScopeSchema
  }),
  z.object({
    type: z.literal("group_teammates"),
    group: automationGroupScopeReferenceSchema,
    playbook_ids: automationPlaybooksScopeSchema
  })
]);
const automationTeammateReportInstanceReferenceSchema = z.object({
  type: z.literal("teammate"),
  teammate_id: teammateIdSchema
});
const automationManagerReportInstanceReferenceSchema = z.object({
  type: z.literal("manager"),
  manager_id: teammateIdSchema
});
const automationGroupReportInstanceReferenceSchema = z.object({
  type: z.literal("group"),
  group_id: groupIdSchema
});
const automationReportInstanceReferenceSchema = z.discriminatedUnion(
  "type",
  [
    automationTeammateReportInstanceReferenceSchema,
    automationManagerReportInstanceReferenceSchema,
    automationGroupReportInstanceReferenceSchema
  ]
);
const instanceRecipientRoutingSchema = z.object({
  type: z.literal("instance_recipient"),
  subscribers: automationRecipientSelectionSchema
});
const automationReportRoutingRuleSchema = z.discriminatedUnion("type", [
  ...automationReportDestinationSchema.options,
  instanceRecipientRoutingSchema,
  z.object({
    type: z.literal("mapped"),
    entries: z.array(
      z.object({
        instance: automationReportInstanceReferenceSchema,
        destination: automationMappedEntryDestinationSchema
      })
    )
  })
]);
const groupReportRoutingSchema = z.discriminatedUnion("type", [
  ...automationReportDestinationSchema.options,
  z.object({
    type: z.literal("mapped"),
    entries: z.array(
      z.object({
        instance: automationGroupReportInstanceReferenceSchema,
        destination: automationMappedEntryDestinationSchema
      })
    ).refine(
      (entries) => new Set(entries.map(({ instance }) => instance.group_id)).size === entries.length,
      { message: "Mapped routing may contain only one entry per group" }
    )
  })
]);
const managerReportRoutingSchema = z.discriminatedUnion("type", [
  ...automationReportDestinationSchema.options,
  instanceRecipientRoutingSchema,
  z.object({
    type: z.literal("mapped"),
    entries: z.array(
      z.object({
        instance: automationManagerReportInstanceReferenceSchema,
        destination: automationMappedEntryDestinationSchema
      })
    ).refine(
      (entries) => new Set(entries.map(({ instance }) => instance.manager_id)).size === entries.length,
      { message: "Mapped routing may contain only one entry per manager" }
    )
  })
]);
const automationReportSynthesisSchema = z.discriminatedUnion(
  "group_by",
  [
    z.object({
      group_by: z.literal("manager"),
      routing: instanceRecipientRoutingSchema,
      /**
       * Which people may receive a manager synthesis, resolved against who
       * actually manages a team at plan time. Absent means every manager of a
       * planned teammate — the shape written before managers were individually
       * selectable, which every existing definition keeps meaning.
       */
      selection: z.object({
        type: z.literal("specific"),
        teammate_ids: z.array(teammateIdSchema)
      }).optional()
    }),
    z.object({
      group_by: z.literal("group"),
      routing: groupReportRoutingSchema
    })
  ]
);
const automationReportDefinitionBaseSchema = z.object({
  instances: automationReportInstancesSchema,
  scope: automationReportScopeRuleSchema,
  routing: automationReportRoutingRuleSchema,
  synthesis: automationReportSynthesisSchema.optional()
});
function checkRoutingUsesChannels(routing) {
  switch (routing.type) {
    case "channels":
      return true;
    case "mapped":
      return routing.entries.some(
        (entry) => entry.destination.type === "channels"
      );
    case "recipients":
    case "instance_recipient":
      return false;
    default:
      return assertUnreachable(routing);
  }
}
const sharedReportDefinitionSchema = automationReportDefinitionBaseSchema.extend({
  instances: z.object({ type: z.literal("shared") }),
  scope: z.object({
    type: z.literal("playbook_teammates"),
    playbook_ids: automationPlaybooksScopeSchema,
    teammate_selection: allOrSpecificTeammatesSchema.optional()
  }),
  routing: automationReportDestinationSchema,
  // Syntheses only exist over a per-teammate fan-out.
  synthesis: z.undefined().optional()
}).refine(
  (definition) => {
    const destination = definition.routing;
    return destination.type !== "recipients" || destination.subscribers.teammate_ids.length === 0 && destination.subscribers.group_ids.length === 0;
  },
  {
    path: ["routing", "subscribers"],
    message: "Custom automation subscribers are not supported yet"
  }
);
const perPersonReportDefinitionSchema = automationReportDefinitionBaseSchema.extend({
  instances: z.object({
    type: z.literal("teammates"),
    // Every teammate, or a hand-picked set. The membership-derived kinds
    // (playbook members, group members) stay out of the form-authored shape:
    // they read as "one result per group", which is a different product.
    selection: allOrSpecificTeammatesSchema
  }),
  scope: z.object({
    type: z.literal("teammates"),
    teammate: z.object({ source: z.literal("instance") }),
    playbook_ids: automationPlaybooksScopeSchema
  }),
  routing: instanceRecipientRoutingSchema
});
const perManagerReportDefinitionSchema = automationReportDefinitionBaseSchema.extend({
  instances: z.object({
    type: z.literal("managers"),
    selection: automationManagerReportInstanceSelectionSchema
  }),
  scope: z.object({
    type: z.literal("manager_teammates"),
    manager: z.object({ source: z.literal("instance") }),
    playbook_ids: automationPlaybooksScopeSchema
  }),
  routing: managerReportRoutingSchema,
  // Syntheses only exist over a per-teammate fan-out.
  synthesis: z.undefined().optional()
});
const perGroupReportDefinitionSchema = automationReportDefinitionBaseSchema.extend({
  instances: z.object({
    type: z.literal("groups"),
    selection: automationGroupReportInstanceSelectionSchema
  }),
  scope: z.object({
    type: z.literal("group_teammates"),
    group: z.object({ source: z.literal("instance") }),
    playbook_ids: automationPlaybooksScopeSchema
  }),
  routing: groupReportRoutingSchema,
  // Syntheses only exist over a per-teammate fan-out.
  synthesis: z.undefined().optional()
});
const automationReportDefinitionSchema = z.union([
  sharedReportDefinitionSchema,
  perPersonReportDefinitionSchema,
  perManagerReportDefinitionSchema,
  perGroupReportDefinitionSchema
]);
function checkIsFormAuthoredReportDefinition(definition) {
  return definition.instances.type === "shared" || definition.instances.type === "teammates" || definition.instances.type === "managers";
}
function checkIsSharedReportDefinition(definition) {
  return definition.instances.type === "shared";
}
const automationReportTransportSchema = z.object({
  provider: automationDeliveryProviderSchema
});
function hydrateLegacyCustomAutomationSettings(value) {
  let hydrated = value;
  if (value !== null && typeof value === "object" && !("report_definition" in value) && "reports" in value) {
    const legacy = value;
    if (Array.isArray(legacy.reports) && legacy.reports.length === 1) {
      const { reports, ...rest } = legacy;
      const report = reports[0];
      hydrated = {
        ...rest,
        report_definition: {
          instances: { type: "shared" },
          scope: {
            type: "playbook_teammates",
            playbook_ids: report?.scope?.playbook_ids
          },
          routing: report?.destination
        }
      };
    }
  }
  if (hydrated !== null && typeof hydrated === "object" && !("transport" in hydrated) && "delivery" in hydrated) {
    hydrated = {
      ...hydrated,
      transport: hydrated.delivery
    };
  }
  return hydrated;
}
const automationReportAgentSettingsSchema = z.object({
  instructions: z.string(),
  // When the agent runs. Hourly runs only when there's new call activity;
  // the rest run at their configured local time. Which frequencies a
  // template's editor offers is form knowledge, not schema knowledge, so
  // every template persists the one shared union.
  trigger: recurringScheduleSchema,
  report_definition: automationReportDefinitionSchema,
  transport: automationReportTransportSchema,
  /**
   * Set for the third result type, whose shape and delivery are described in
   * `instructions` rather than configured — the one result type that cannot be
   * read back off `report_definition`, which is left as the editor last
   * configured it so switching back restores that configuration. Each run
   * derives its own definition and transport from the prompt instead.
   *
   * This is kept separately for flexibility. The settings schema has changed a
   * lot in v0 and a separate field is easier to migrate later.
   */
  is_custom_result_mode: z.boolean().optional()
}).refine(
  (settings) => {
    const { report_definition } = settings;
    return settings.transport.provider !== AutomationDeliveryProvider.EMAIL || !checkRoutingUsesChannels(report_definition.routing) && (report_definition.synthesis === void 0 || !checkRoutingUsesChannels(report_definition.synthesis.routing));
  },
  {
    path: ["report_definition", "routing"],
    message: "Email delivery does not support channel destinations"
  }
);
const customAutomationSettingsSchema = z.preprocess(
  hydrateLegacyCustomAutomationSettings,
  automationReportAgentSettingsSchema
);
const managerReportAgentSettingsSchema = automationReportAgentSettingsSchema.refine(
  (settings) => settings.report_definition.instances.type === "managers",
  {
    path: ["report_definition", "instances"],
    message: "Configured manager-report templates require manager instances"
  }
);
function getAutomationDeliveryEditorValue(settings) {
  const { provider } = settings.transport;
  const definition = getAutomationReportDefinition(settings);
  if (provider === AutomationDeliveryProvider.EMAIL) {
    return { provider };
  }
  return {
    provider,
    destination: definition.routing.type === "channels" ? {
      kind: "channel",
      channels: definition.routing.channels
    } : { kind: "direct_message" }
  };
}
function updateAutomationDeliveryEditorValue(settings, delivery) {
  return {
    ...settings,
    transport: { provider: delivery.provider },
    report_definition: updateAutomationReportRouting(
      settings.report_definition,
      delivery
    )
  };
}
function updateAutomationReportRouting(definition, delivery) {
  const channelDestination = delivery.provider !== AutomationDeliveryProvider.EMAIL && delivery.destination.kind === "channel" ? delivery.destination : null;
  if (definition.instances.type === "managers") {
    if (channelDestination) {
      return automationReportDefinitionSchema.parse({
        ...definition,
        routing: { type: "channels", channels: channelDestination.channels }
      });
    }
    if (definition.routing.type === "instance_recipient") {
      return definition;
    }
    return automationReportDefinitionSchema.parse({
      ...definition,
      routing: {
        type: "instance_recipient",
        subscribers: { teammate_ids: [], group_ids: [] }
      }
    });
  }
  if (!checkIsSharedReportDefinition(definition)) {
    return definition;
  }
  if (channelDestination) {
    return {
      ...definition,
      routing: { type: "channels", channels: channelDestination.channels }
    };
  }
  switch (definition.routing.type) {
    case "recipients":
      return definition;
    case "channels":
      return {
        ...definition,
        routing: {
          type: "recipients",
          primary: { teammate_ids: [], group_ids: [] },
          subscribers: { teammate_ids: [], group_ids: [] }
        }
      };
    default:
      return assertUnreachable(definition.routing);
  }
}
function getAutomationManagerRecipientsEditorValue(definition) {
  return definition.instances.type === "managers" && definition.instances.selection.type === "specific" ? {
    scope: "specific_managers",
    teammate_ids: definition.instances.selection.teammate_ids
  } : { scope: "all_managers" };
}
function updateAutomationManagerRecipientsEditorValue(settings, recipients) {
  const definition = settings.report_definition;
  if (definition.instances.type !== "managers") {
    return settings;
  }
  return {
    ...settings,
    report_definition: automationReportDefinitionSchema.parse({
      ...definition,
      instances: {
        type: "managers",
        selection: recipients.scope === "all_managers" ? { type: "all" } : { type: "specific", teammate_ids: recipients.teammate_ids }
      }
    })
  };
}
function getAutomationSubscribersEditorValue(definition) {
  return definition.routing.type === "instance_recipient" ? { teammate_ids: definition.routing.subscribers.teammate_ids } : { teammate_ids: [] };
}
function updateAutomationSubscribersEditorValue(settings, subscribers) {
  const definition = settings.report_definition;
  if (definition.routing.type !== "instance_recipient") {
    return settings;
  }
  return {
    ...settings,
    report_definition: automationReportDefinitionSchema.parse({
      ...definition,
      routing: {
        ...definition.routing,
        subscribers: {
          teammate_ids: subscribers.teammate_ids,
          group_ids: definition.routing.subscribers.group_ids
        }
      }
    })
  };
}
const allAutomationResultModes = ["shared", "own", "custom"];
const automationResultModeSchema = z.enum(allAutomationResultModes);
const allAutomationResultSelectionModes = ["all", "specific"];
const automationResultSelectionModeSchema = z.enum(
  allAutomationResultSelectionModes
);
function getAutomationResultSelectionMode(definition) {
  return definition.instances.type !== "shared" ? definition.instances.selection.type : "specific";
}
function updateAutomationResultSelectionMode(settings, selectionMode) {
  const definition = settings.report_definition;
  if (definition.instances.type === "shared" || getAutomationResultSelectionMode(definition) === selectionMode) {
    return settings;
  }
  const instances = (() => {
    switch (definition.instances.type) {
      case "teammates":
        return {
          type: "teammates",
          selection: selectionMode === "all" ? { type: "all" } : {
            type: "specific",
            teammate_ids: [],
            group_ids: []
          }
        };
      case "managers":
        return {
          type: "managers",
          selection: selectionMode === "all" ? { type: "all" } : { type: "specific", teammate_ids: [] }
        };
      case "groups":
        return {
          type: "groups",
          selection: selectionMode === "all" ? { type: "all" } : { type: "specific", group_ids: [] }
        };
      default:
        return assertUnreachable(definition.instances);
    }
  })();
  return {
    ...settings,
    report_definition: automationReportDefinitionSchema.parse({
      ...definition,
      instances
    })
  };
}
function getAutomationRecipientSelection(definition) {
  const emptySelection = { teammate_ids: [], group_ids: [] };
  switch (definition.instances.type) {
    case "shared":
      return definition.routing.type === "recipients" ? definition.routing.primary : emptySelection;
    case "teammates":
      return definition.instances.selection.type === "specific" ? {
        teammate_ids: definition.instances.selection.teammate_ids,
        group_ids: definition.instances.selection.group_ids
      } : emptySelection;
    case "managers":
      return definition.instances.selection.type === "specific" ? {
        teammate_ids: definition.instances.selection.teammate_ids,
        group_ids: []
      } : emptySelection;
    case "groups":
      return definition.instances.selection.type === "specific" ? {
        teammate_ids: [],
        group_ids: definition.instances.selection.group_ids
      } : emptySelection;
    default:
      return assertUnreachable(definition.instances);
  }
}
function buildAutomationReportDefinition(mode, playbookIds, selection) {
  if (mode === "own") {
    return {
      instances: { type: "teammates", selection: { type: "all" } },
      scope: {
        type: "teammates",
        teammate: { source: "instance" },
        playbook_ids: [...playbookIds]
      },
      routing: {
        type: "instance_recipient",
        subscribers: { teammate_ids: [], group_ids: [] }
      },
      synthesis: {
        group_by: "manager",
        routing: {
          type: "instance_recipient",
          subscribers: { teammate_ids: [], group_ids: [] }
        }
      }
    };
  }
  return {
    instances: { type: "shared" },
    scope: {
      type: "playbook_teammates",
      playbook_ids: [...playbookIds]
    },
    routing: {
      type: "recipients",
      primary: selection,
      subscribers: { teammate_ids: [], group_ids: [] }
    }
  };
}
function getAutomationResultMode(settings) {
  if (settings.is_custom_result_mode) {
    return "custom";
  }
  return settings.report_definition.instances.type === "shared" ? "shared" : "own";
}
function updateAutomationResultMode(settings, mode) {
  if (mode === "custom") {
    return { ...settings, is_custom_result_mode: true };
  }
  const { is_custom_result_mode: _custom, ...configured } = settings;
  const definition = configured.report_definition;
  const isSharedDefinition = checkIsSharedReportDefinition(definition);
  if (mode === "own" && !isSharedDefinition) {
    return configured;
  }
  if (mode === "shared" && isSharedDefinition) {
    return configured;
  }
  return {
    ...configured,
    report_definition: buildAutomationReportDefinition(
      mode,
      [...definition.scope.playbook_ids],
      getAutomationRecipientSelection(definition)
    )
  };
}
function partitionAutomationPeopleSelection(people, managerTeammateIds) {
  const managerIdSet = new Set(managerTeammateIds);
  const managerIds = people.includesAllManagers ? [] : people.teammateIds.filter((teammateId) => managerIdSet.has(teammateId));
  const teammateIds = people.includesAllTeammates ? [] : people.teammateIds.filter((teammateId) => !managerIdSet.has(teammateId));
  return {
    teammateIds,
    managerIds,
    hasTeammates: people.includesAllTeammates || teammateIds.length > 0,
    hasManagers: people.includesAllManagers || managerIds.length > 0
  };
}
function getAutomationPeopleSelection(definition) {
  const { instances } = definition;
  switch (instances.type) {
    case "teammates": {
      const synthesis = definition.synthesis?.group_by === "manager" ? definition.synthesis : void 0;
      const selectedIds = instances.selection.type === "specific" ? instances.selection.teammate_ids : [];
      return {
        includesAllTeammates: instances.selection.type === "all",
        // A synthesis without a `selection` is the stored form of "All
        // managers" — the shape the form writes for that option, and the one
        // every checkbox-era config already has.
        includesAllManagers: synthesis !== void 0 && synthesis.selection === void 0,
        teammateIds: [
          .../* @__PURE__ */ new Set([
            ...selectedIds,
            ...synthesis?.selection?.teammate_ids ?? []
          ])
        ]
      };
    }
    case "managers":
      return {
        includesAllTeammates: false,
        includesAllManagers: instances.selection.type === "all",
        teammateIds: instances.selection.type === "specific" ? instances.selection.teammate_ids : []
      };
    case "shared":
    case "groups":
      return {
        includesAllTeammates: false,
        includesAllManagers: false,
        teammateIds: []
      };
    default:
      return assertUnreachable(instances);
  }
}
function updateAutomationPeopleSelection(settings, people, managerTeammateIds) {
  const definition = settings.report_definition;
  if (!checkIsFormAuthoredReportDefinition(definition) || checkIsSharedReportDefinition(definition)) {
    return settings;
  }
  const { includesAllTeammates, includesAllManagers } = people;
  const {
    teammateIds: pickedTeammateIds,
    managerIds: pickedManagerIds,
    hasTeammates,
    hasManagers
  } = partitionAutomationPeopleSelection(people, managerTeammateIds);
  const playbookIds = [...definition.scope.playbook_ids];
  const subscribers = definition.routing.type === "instance_recipient" ? definition.routing.subscribers : { teammate_ids: [], group_ids: [] };
  const routing = { type: "instance_recipient", subscribers };
  if (hasManagers && !hasTeammates) {
    return {
      ...settings,
      report_definition: automationReportDefinitionSchema.parse({
        instances: {
          type: "managers",
          selection: includesAllManagers ? { type: "all" } : { type: "specific", teammate_ids: pickedManagerIds }
        },
        scope: {
          type: "manager_teammates",
          manager: { source: "instance" },
          playbook_ids: playbookIds
        },
        routing
      })
    };
  }
  const existingSynthesis = definition.instances.type === "teammates" ? definition.synthesis : void 0;
  const synthesisRouting = existingSynthesis?.group_by === "manager" ? existingSynthesis.routing : {
    type: "instance_recipient",
    subscribers: { teammate_ids: [], group_ids: [] }
  };
  const synthesis = existingSynthesis?.group_by === "group" ? existingSynthesis : includesAllManagers ? { group_by: "manager", routing: synthesisRouting } : pickedManagerIds.length > 0 ? {
    group_by: "manager",
    routing: synthesisRouting,
    selection: {
      type: "specific",
      teammate_ids: pickedManagerIds
    }
  } : void 0;
  return {
    ...settings,
    report_definition: automationReportDefinitionSchema.parse({
      instances: {
        type: "teammates",
        selection: includesAllTeammates ? { type: "all" } : {
          type: "specific",
          teammate_ids: pickedTeammateIds,
          group_ids: []
        }
      },
      scope: {
        type: "teammates",
        teammate: { source: "instance" },
        playbook_ids: playbookIds
      },
      routing,
      synthesis
    })
  };
}
function updateAutomationRecipientSelection(settings, selection) {
  const definition = settings.report_definition;
  if (definition.instances.type !== "shared") {
    const instances = (() => {
      switch (definition.instances.type) {
        case "teammates":
          return {
            type: "teammates",
            selection: { type: "specific", ...selection }
          };
        case "managers":
          return {
            type: "managers",
            selection: {
              type: "specific",
              teammate_ids: selection.teammate_ids
            }
          };
        case "groups":
          return {
            type: "groups",
            selection: {
              type: "specific",
              group_ids: selection.group_ids
            }
          };
        default:
          return assertUnreachable(definition.instances);
      }
    })();
    return {
      ...settings,
      report_definition: automationReportDefinitionSchema.parse({
        ...definition,
        instances
      })
    };
  }
  return {
    ...settings,
    report_definition: automationReportDefinitionSchema.parse({
      ...definition,
      routing: {
        type: "recipients",
        primary: selection,
        subscribers: definition.routing.type === "recipients" ? definition.routing.subscribers : { teammate_ids: [], group_ids: [] }
      }
    })
  };
}
function updateAutomationPlaybookIds(settings, playbookIds) {
  const definition = settings.report_definition;
  return {
    ...settings,
    report_definition: automationReportDefinitionSchema.parse({
      ...definition,
      scope: {
        ...definition.scope,
        playbook_ids: [...playbookIds]
      }
    })
  };
}
function getAutomationReportDefinition(settings) {
  return settings.report_definition;
}
function checkIsTeammateCoachingSettings(settings) {
  return "coaching_focus" in settings;
}
function getAutomationPlaybookIds(settings) {
  if ("report_definition" in settings) {
    const definition = getAutomationReportDefinition(settings);
    return [...definition.scope.playbook_ids];
  }
  return [settings.playbook_id];
}
function getAutomationPrimaryPlaybookId(settings) {
  return getAutomationPlaybookIds(settings)[0];
}
function getAutomationSettingsSchema(templateId) {
  switch (templateId) {
    case AutomationTemplateIds.TEAMMATE_COACHING:
      return teammateCoachingAutomationSettingsSchema;
    case AutomationTemplateIds.PLAYBOOK_SCORE_MOVEMENT:
    case AutomationTemplateIds.LEAD_BURNING:
      return managerReportAgentSettingsSchema;
    case AutomationTemplateIds.CUSTOMER_PRAISE_DIGEST:
    case AutomationTemplateIds.PERFECT_CALLS:
    case AutomationTemplateIds.CUSTOM:
      return customAutomationSettingsSchema;
    default:
      assertUnreachable(templateId);
  }
}
const automationTemplates = [
  {
    id: AutomationTemplateIds.TEAMMATE_COACHING,
    name: "Weekly coaching goals",
    description: "Sends weekly coaching goals to teammates, managers, or both. Managers can add guidance on what to focus on.",
    overviewMarkdown: `Alpharun generates weekly coaching goals for every teammate with enough call activity and shows them on the teammate profile. This agent sends those goals on a Monday and Friday at 8am to teammates, managers, or both, and lets managers add coaching focus guidance so Alpharun prioritizes the criteria that matter most.

#### What Alpharun uses

- Each teammate's AI-generated weekly coaching goals
- How each teammate is performing against each playbook criteria
- Which behaviors are having the biggest impact on their score
- What top teammates are doing differently
- Each teammate's week-over-week performance metrics

#### What this agent controls

- Monday and Friday email delivery
- Whether teammates, managers, or both receive the goals
- A manager digest with every teammate's goals in one view
- Optional coaching focus guidance for how goals should be prioritized`
  },
  {
    id: AutomationTemplateIds.CUSTOMER_PRAISE_DIGEST,
    name: "Customer praise digest",
    description: "Finds meaningful moments when customers praise your product, service, or team. It captures what stood out in their own words.",
    overviewMarkdown: `Finds moments where customers go noticeably out of their way to praise your company, product, service, or team. Each result includes the customer's words, what they praised, helpful context, and a link to the call.

#### What it looks at

- Meaningful product or service outcomes
- Favorable comparisons to alternatives or competitors
- Unprompted praise for a specific teammate
- Strong expressions of trust, relief, confidence, or appreciation

#### What you'll get

- A shared digest of every meaningful praise moment found
- The customer's quote and what they were praising
- A short explanation of why the moment matters
- A link to the exact moment in the call`
  },
  {
    id: AutomationTemplateIds.PERFECT_CALLS,
    name: "Perfect calls",
    description: "Finds calls with a 100% overall playbook score and highlights the behaviors behind excellent teammate performance.",
    overviewMarkdown: `Finds calls with a 100% overall playbook score and highlights the strongest examples of excellent teammate performance. Each result explains what the teammate did to earn the score and connects their behavior to the customer's response.

#### What it looks at

- Calls with a 100% overall playbook score
- Specific actions and techniques that demonstrate excellent execution
- Teammate language that clearly shows the behavior
- Customer language that shows the impact, when available

#### What you'll get

- The teammate's name and a link to the call
- A concise explanation of what earned the perfect score
- One short quote from the teammate
- One short customer quote showing the impact, when available`
  },
  {
    id: AutomationTemplateIds.PLAYBOOK_SCORE_MOVEMENT,
    name: "Playbook score movement alert",
    description: "Tracks weekly changes in your team's playbook scores, who drove them, and what to do next.",
    overviewMarkdown: `Tracks your team's playbook criteria scores week over week and surfaces what moved, which reps drove it, and what to do about it.

#### What it looks at

- Criteria scores at the team level compared to the prior period
- Which individual reps are driving team-level movements up or down
- The specific behaviors on calls that explain each score change

#### What you'll get

- A summary of overall playbook adherence movement for the period
- One section per criteria that moved, with scores and the behavioral explanation
- A reinforcement or coaching action for each movement so you know exactly what to do next`
  },
  {
    id: AutomationTemplateIds.LEAD_BURNING,
    name: "Lead burning alert",
    description: "Alerts managers when teammates move through live leads without meaningful conversations.",
    overviewMarkdown: `Periodically analyzes your team's calls to identify team members who are cycling through live leads without meaningful engagement. When a pattern is detected, managers receive an alert with call evidence, transcript examples, and a recommended action so they can intervene quickly.

#### What it looks at

- Team members with a high share of ultra-short live calls relative to their peers
- Clusters of consecutive burned leads within a short time window
- Calls where early playbook sections are marked Not Applicable, confirming no real conversation started

#### What you'll get

- A flagged list of team members showing their short-call rate compared to the team average
- Transcript quotes from burned calls \u2014 only from calls where a live customer picked up and a real conversation failed to start, never voicemails or no-answers
- A recommended action for each flagged team member so managers know exactly what to do
- Notifications only when lead burning is detected`
  },
  {
    id: AutomationTemplateIds.CUSTOM,
    name: "Custom agent",
    description: "Looks for anything you describe in selected calls and shares a summary with supporting call moments.",
    overviewMarkdown: `Custom agents watch your calls for a specific pattern, risk, opportunity, or theme that you describe in plain language. When something is found, Alpharun sends the right people a summary with call evidence.

#### What it looks at

- The calls on the playbook you choose, over the schedule you set
- Whatever pattern, risk, opportunity, or theme you describe \u2014 an objection, a missed compliance step, a competitor mention, or a great call worth celebrating

#### What you'll get

- One summary per run with exactly what you asked to receive
- Call evidence and quotes backing each finding
- Delivery by email to the teammates and groups you select
- On frequent schedules, a notification only when something qualifies`
  }
];
function getAutomationTemplateById(templateId) {
  const template = automationTemplates.find(({ id }) => id === templateId);
  if (!template) {
    throw new Error(`Unknown automation template: ${templateId}`);
  }
  return template;
}
function buildDefaultManagerReportDefinition(playbookId) {
  return {
    instances: { type: "managers", selection: { type: "all" } },
    scope: {
      type: "manager_teammates",
      manager: { source: "instance" },
      playbook_ids: [playbookId]
    },
    routing: {
      type: "instance_recipient",
      subscribers: { teammate_ids: [], group_ids: [] }
    }
  };
}
function buildDefaultAutomationSettings(templateId, playbookId) {
  switch (templateId) {
    case AutomationTemplateIds.TEAMMATE_COACHING:
      return {
        playbook_id: playbookId,
        coaching_focus: "",
        should_notify_teammates: false,
        should_notify_managers: false
      };
    case AutomationTemplateIds.PLAYBOOK_SCORE_MOVEMENT:
      return {
        instructions: "",
        trigger: {
          frequency: "weekly",
          weekdays: ["monday"],
          time_of_day: "09:00"
        },
        report_definition: buildDefaultManagerReportDefinition(playbookId),
        transport: { provider: AutomationDeliveryProvider.EMAIL }
      };
    case AutomationTemplateIds.LEAD_BURNING:
      return {
        instructions: "",
        trigger: {
          frequency: "daily",
          time_of_day: LEAD_BURNING_DAILY_TIME_OF_DAY
        },
        report_definition: buildDefaultManagerReportDefinition(playbookId),
        transport: { provider: AutomationDeliveryProvider.EMAIL }
      };
    case AutomationTemplateIds.CUSTOMER_PRAISE_DIGEST:
    case AutomationTemplateIds.PERFECT_CALLS:
      return {
        instructions: "",
        trigger: {
          frequency: "weekly",
          weekdays: ["friday"],
          time_of_day: "17:00"
        },
        report_definition: {
          instances: { type: "shared" },
          scope: {
            type: "playbook_teammates",
            playbook_ids: [playbookId]
          },
          routing: {
            type: "recipients",
            primary: {
              teammate_ids: [],
              group_ids: []
            },
            subscribers: {
              teammate_ids: [],
              group_ids: []
            }
          }
        },
        transport: { provider: AutomationDeliveryProvider.EMAIL }
      };
    case AutomationTemplateIds.CUSTOM:
      return {
        instructions: "",
        trigger: {
          frequency: "daily",
          time_of_day: "09:00"
        },
        report_definition: {
          instances: { type: "shared" },
          scope: {
            type: "playbook_teammates",
            playbook_ids: [playbookId]
          },
          routing: {
            type: "recipients",
            primary: {
              teammate_ids: [],
              group_ids: []
            },
            subscribers: {
              teammate_ids: [],
              group_ids: []
            }
          }
        },
        transport: { provider: AutomationDeliveryProvider.EMAIL }
      };
    default:
      assertUnreachable(templateId);
  }
}
export {
  AutomationDeliveryProvider,
  AutomationTemplateIds,
  DEFAULT_AUTOMATION_DELIVERY,
  LEAD_BURNING_DAILY_TIME_OF_DAY,
  allAutomationManagerScopes,
  allAutomationResultModes,
  allAutomationResultSelectionModes,
  allAutomationTemplateIds,
  asAutomationId,
  automationChatDeliveryModeSchema,
  automationDeliveryProviderSchema,
  automationDeliverySchema,
  automationGroupReportInstanceSelectionSchema,
  automationIdSchema,
  automationManagerRecipientsSchema,
  automationManagerReportInstanceSelectionSchema,
  automationManagerScopeSchema,
  automationMappedEntryDestinationSchema,
  automationPlaybooksScopeSchema,
  automationProviderChannelSchema,
  automationRecipientSelectionSchema,
  automationReportAgentSettingsSchema,
  automationReportDefinitionSchema,
  automationReportDestinationSchema,
  automationReportInstanceReferenceSchema,
  automationReportInstancesSchema,
  automationReportRoutingRuleSchema,
  automationReportScopeRuleSchema,
  automationReportSynthesisSchema,
  automationResultModeSchema,
  automationResultSelectionModeSchema,
  automationSubscriberRecipientsSchema,
  automationTemplateIdSchema,
  automationTemplates,
  buildDefaultAutomationDeliveryForProvider,
  buildDefaultAutomationSettings,
  checkIsTeammateCoachingSettings,
  checkRoutingUsesChannels,
  customAutomationSettingsSchema,
  encodeAutomationResourceId,
  getAutomationDeliveryEditorValue,
  getAutomationManagerRecipientsEditorValue,
  getAutomationPeopleSelection,
  getAutomationPlaybookIds,
  getAutomationPrimaryPlaybookId,
  getAutomationRecipientSelection,
  getAutomationReportDefinition,
  getAutomationResultMode,
  getAutomationResultSelectionMode,
  getAutomationSettingsSchema,
  getAutomationSubscribersEditorValue,
  getAutomationTemplateById,
  managerReportAgentSettingsSchema,
  partitionAutomationPeopleSelection,
  teammateCoachingAutomationSettingsSchema,
  updateAutomationDeliveryEditorValue,
  updateAutomationManagerRecipientsEditorValue,
  updateAutomationPeopleSelection,
  updateAutomationPlaybookIds,
  updateAutomationRecipientSelection,
  updateAutomationResultMode,
  updateAutomationResultSelectionMode,
  updateAutomationSubscribersEditorValue
};
//# sourceMappingURL=automation.js.map
